import { useEffect } from 'react';

import { envConfig } from '#constants/envConfig';
import { buildLocaleSwitchPath, getLocaleFromPath, isLocaleValid } from '../utils';
import { getNativeLocale, readLocaleCookie, setLocaleCookie } from '../utils/nativeLocale';

/**
 * Restores the chosen language on a cold start of the native shell. No-op on web.
 *
 * The app always boots at `/` (the static SPA shell), and the boot-time locale comes
 * from the `locale` cookie read synchronously in `src/client.tsx`. Android flushes the
 * WebView cookie jar lazily, so a choice made shortly before the app was killed can be
 * gone on the next launch — and the app would silently come back in English.
 *
 * This only fills a *missing* cookie from native storage; a cookie that is present is
 * left alone, so an explicit in-session switch is never overridden. A reload happens
 * only when the current URL is on the wrong locale, which keeps the common case
 * (cookie intact, or stored locale already active) free of any flicker.
 */
export const useNativeLocaleSync = () => {
  useEffect(() => {
    if (!envConfig.IS_NATIVE || readLocaleCookie()) {
      return;
    }

    let active = true;

    void (async () => {
      const stored = await getNativeLocale();
      if (!stored || !isLocaleValid(stored)) return;

      if (!active || !stored || stored === getLocaleFromPath(window.location)) {
        // Nothing stored, or the URL already carries it — just re-seed the cookie.
        if (active && stored) {
          setLocaleCookie(stored);
        }
        return;
      }

      setLocaleCookie(stored);
      const path = buildLocaleSwitchPath(window.location.pathname, stored);
      window.location.assign(path + window.location.search + window.location.hash);
    })();

    return () => {
      active = false;
    };
  }, []);
};
